// Agent 在浏览器里跑。后端只转发模型请求，不持有会话，也不执行编辑器工具。
// 画布工具见 editor_tools.js。deepagents 自带的 ls/read_file 等是虚拟文件系统，
// web 端没有这套工作区，用 harness profile 从模型可见工具里拿掉。

import { createDeepAgent, registerHarnessProfile } from 'deepagents/browser';
import { HumanMessage } from '@langchain/core/messages';
import { dynamicSystemPromptMiddleware } from 'langchain';
import { EDITING_SOP } from '../../app/agent/editing_sop.js';
import { IndexedDBSaver } from './checkpointer.js';
import { createModel } from './llm.js';
import { EDITOR_TOOLS, readCanvas } from './editor_tools.js';

const FILE_TOOLS = ['ls', 'read_file', 'write_file', 'edit_file', 'delete', 'glob', 'grep', 'execute', 'task'];

registerHarnessProfile('openai', {
    excludedTools: FILE_TOOLS,
    generalPurposeSubagent: { enabled: false },
});

export const SYSTEM_PROMPT = EDITING_SOP;

/**
 * 用给定的 model + tools 创建浏览器端 deep agent。
 * model 由调用方注入（生产走 createModel，测试走 mock model）。
 */
// 跟 app/agent/workflow.js 一样：静态说明留在 systemPrompt，每次模型调用再拼当时的状态。
function canvasPrompt() {
    return dynamicSystemPromptMiddleware(async () => {
        let canvas;
        try { canvas = await readCanvas(); }
        catch (error) { canvas = { loaded: false, message: error.message }; }
        return `当前画布是这次模型调用时的最新轨道。上一轮之后用户可能已经改过时间轴，以这里为准，不要沿用更早对话里的轨道，也不要查询画布。\n${JSON.stringify(canvas)}`;
    });
}

export function createAgent({ model, tools = EDITOR_TOOLS } = {}) {
    return createDeepAgent({
        name: 'nle-assistant',
        model,
        checkpointer: new IndexedDBSaver(),
        systemPrompt: SYSTEM_PROMPT,
        tools,
        middleware: [canvasPrompt()],
    });
}

/**
 * 创建（或复用）浏览器端 deep agent（模型配置从 IndexedDB 读）。
 */
let agentPromise = null;
export async function getAgent() {
    if (!agentPromise) {
        agentPromise = createAgent({ model: await createModel() });
    }
    return agentPromise;
}

/** 重置缓存的 agent（模型配置更新后需要重建 model）。 */
export function resetAgent() {
    agentPromise = null;
}

/**
 * 以消息流方式对话。yield 每个 LangGraph 消息 chunk（AIMessageChunk），
 * 调用方自行渲染文本增量与工具调用。
 */
export async function* streamChat(text, { threadId } = {}) {
    const agent = await getAgent();
    const stream = await agent.stream({ messages: [new HumanMessage(text)] }, {
        configurable: { thread_id: threadId },
        streamMode: 'messages',
    });
    for await (const [chunk] of stream) {
        yield chunk;
    }
}

export default { getAgent, resetAgent, streamChat, SYSTEM_PROMPT };
